import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { takeUntil } from 'rxjs/operators';
import { SchoolUniversity } from '../../../server/models/school/school-university';
import { Subject } from 'rxjs';
import { CurrentSchoolUniversityService } from '../../../server/services/current-school-university.service';
import { Location } from '@angular/common';
import { AuthService } from 'app/@core/services/auth.service';
import { Router } from '@angular/router';
import { menu } from 'app/menu/menu';
import { universityMenu } from 'app/menu/university';
import { schoolMenu } from 'app/menu/school';
import { frontendMenu } from 'app/menu/frontend';
import { internshipSecretaryMenu } from 'app/menu/internship-secretary';
import { tutorMenu } from 'app/menu/tutor';
import { CoreMenu } from '@core/types';

import { TranslateService } from '@ngx-translate/core';

// ContentHeader component interface
export interface ContentHeader {
	headerTitle: string;
	icon?: IconProp | Array<IconProp>;
	btn: {
		visible: boolean;
		disabled?: boolean;
		textDisabled?: string;
		type?: string;
		text?: string;
		routerLink?: string;
		permission?: string | string[];
	};
	actionButton: boolean;
	breadcrumb?: {
		type?: string;
		links?: Array<{
			name?: string;
			isLink?: boolean;
			link?: string;
		}>;
	};
}

@Component({
	selector: 'app-content-header',
	templateUrl: './content-header.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ContentHeaderComponent implements OnInit, OnDestroy {
	@Input() contentHeader: ContentHeader;
	@Output() onBack = new EventEmitter<void>();
	@Output() onCreate = new EventEmitter<void>();
	public currentSchool: SchoolUniversity | null;

	private _unsubscribeAll: Subject<any>;

	constructor(
		private _currentSchoolUniversityService: CurrentSchoolUniversityService,
		private _location: Location,
		private _authService: AuthService,
		private _router: Router,
		private _translateService: TranslateService
	) {
		this._unsubscribeAll = new Subject();
	}

	get backTooltip(): string {
		return this._translateService?.instant('common_btn_back') || 'Indietro';
	}

	get createTooltip(): string {
		return this._translateService?.instant('common_btn_create') || 'Crea';
	}

	ngOnInit() {
		this._currentSchoolUniversityService.currentSchoolSubject.pipe(takeUntil(this._unsubscribeAll)).subscribe(res => {
			this.currentSchool = res;
		});
	}

	goBack() {
		if (this.onBack.observed) {
			this.onBack.emit();
		} else {
			this._location.back();
		}
	}

	get canShowCreateButton(): boolean {
		const button = this.contentHeader?.btn;

		if (!button?.visible || (button.type && button.type !== 'create')) {
			return false;
		}

		if (button.permission) {
			const permissions = Array.isArray(button.permission) ? button.permission : [button.permission];
			return this._authService.hasAnyPermission(permissions);
		}

		const inferredCreatePermission = this.inferCreatePermissionFromRoute();
		if (!inferredCreatePermission) {
			return false;
		}

		return this._authService.hasPermission(inferredCreatePermission);
	}

	private inferCreatePermissionFromRoute(): string | undefined {
		const url = this.normalizeUrl(this._router.url);
		if (!url) {
			return undefined;
		}

		const allMenus: CoreMenu[] = [...menu, ...universityMenu, ...schoolMenu, ...frontendMenu, ...internshipSecretaryMenu, ...tutorMenu];
		const matched = this.findMenuByUrl(allMenus, url);
		const viewPermission = matched?.permissions?.[0];
		if (!viewPermission) {
			return undefined;
		}

		return viewPermission.endsWith('.view') ? viewPermission.replace(/\.view$/, '.create') : viewPermission;
	}

	private findMenuByUrl(items: CoreMenu[], currentUrl: string): CoreMenu | undefined {
		for (const item of items || []) {
			const itemUrl = this.normalizeUrl(item.url);
			if (itemUrl && (itemUrl === currentUrl || currentUrl.startsWith(itemUrl + '/'))) {
				return item;
			}
			if (item.children?.length) {
				const found = this.findMenuByUrl(item.children as CoreMenu[], currentUrl);
				if (found) {
					return found;
				}
			}
		}
		return undefined;
	}

	private normalizeUrl(url?: string): string {
		if (!url) {
			return '';
		}
		return url.replace(/^\/+/, '').split('?')[0].split('#')[0];
	}

	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}
}
